import { c as _c } from "react/compiler-runtime";
/**
 * Analytics Context Provider and Hook
 *
 * This module provides analytics tracking functionality for:
 * - Listing views and searches
 * - Quote views
 * - Reservations
 * - Gift card purchases
 *
 * The analytics data is pushed to analytics pipelines via the dataLayer.
 * @see https://www.notion.so/Events-Variables-and-Dimensions-153301efe8e440c186e835414fddc4a6
 */
// Third Party
import { Account, GiftCard, Listing, PriceDisplay, QuoteItemizedType, ListingsSearchType, TravelerSurveyChoice } from '@gql';
import { isbot } from 'isbot';
import { toInteger } from 'lodash';
import sha256 from 'crypto-js/sha256';

// Utils
import * as React from 'react';
import { formatDollarAmountNumberOnly } from '@utils/money';
import { getSearchSession, setSearchSession } from '@utils/search-session';
import { useSession } from '@lib/auth/client';
import dayjs from '@utils/dayjs-setup';
import { quoteValidationReportClause } from '@utils/quote-validation-message';
import type { SearchQueryParams } from './search-context-utils';

// Types
export type AnalyticsContext = {
  setCurrentListing: (listing: AnalyticsListing) => void;
  setCurrentExperiments: (experiments: string | null) => void;
  setListings: (source: ListingListSource, listings: AnalyticsListing[]) => void;
  setSearch: (search: AnalyticsSearch | null) => void;
  emitViewQuoteEvent: (quote: AnalyticsQuote) => void;
  emitViewQuoteComparisonEvent: (quote: AnalyticsQuote, comparisonQuotes: AnalyticsQuoteComparison[]) => void;
  emitReservationPurchaseEvent: (reservation: AnalyticsReservation) => void;
  emitGiftCardPurchaseEvent: (giftCard: AnalyticsGiftCard) => void;
  emitBeginQuoteCheckoutEvent: (quote: AnalyticsQuote) => void;
  emitGenerateLeadEvent: (lead: {
    source: string;
    account_name: string;
  }) => void;
  emitReferralJoinedProgramEvent: () => void;
  emitSignUpBeginEvent: (provider: string) => void;
  emitSignUpEvent: (user: AnalyticsUser, rewardCampaign?: string) => void;
  emitQuoteValidationFallbackEvent: (details: QuoteValidationFallbackDetails) => void;
  emitSearchCheckInTooSoonEvent: (details: SearchCheckInTooSoonDetails) => void;
  emitSearchQueryResultsEvent: (details: SearchQueryResultsDetails) => void;
  emitAccountActionFallbackEvent: (details: AccountActionFallbackDetails) => void;
  emitTravelerSurveyEvent: (details: TravelerSurveyEventDetails) => void;
  emitTravelerGovIdVerificationEvent: (details: TravelerGovIdVerificationEventDetails) => void;
};
export type QuoteValidationFallbackDetails = {
  rawMessage: string;
  listingSlug: string;
  checkInDate: string | null;
  checkOutDate: string | null;
  adultCount: number;
  childCount: number;
  infantCount: number;
  petCount: number;
};
export type SearchCheckInTooSoonDetails = {
  checkInDate: string;
  checkOutDate: string | null;
  firstBookableCheckInDate: string;
  /** Where the guest was when the boundary was hit: `search` or `account`. */
  surface: 'search' | 'account';
};
export type SearchQueryResultsDetails = {
  /** 1 for the first result set since the search page mounted. */
  queryIndex: number;
  /**
   * Total matches across all pages, not the page size. listing_engine stops
   * counting at 1000, so 1000 means "1000 or more".
   */
  resultCount: number | null;
  destination: string | null;
  query: SearchQueryParams;
};
export type AccountActionFallbackDetails = {
  step: 'terms' | 'card' | 'new_link';
  failure: 'request_failed' | 'empty_result' | 'unexpected_redirect';
};
export type TravelerSurveyEventDetails = {
  outcome: 'answered' | 'submit_rejected' | 'submit_abandoned';
  choice?: TravelerSurveyChoice;
  message?: string;
};
export type TravelerGovIdVerificationEventDetails = {
  outcome: 'started' | 'verified' | 'cancelled' | 'declined' | 'failed' | 'expired' | 'timeout' | 'error';
  message?: string;
};
export type AnalyticsListing = Pick<Listing, 'name' | 'slug' | 'id' | 'listingLevel' | 'searchPromotionType'> & {
  account: Pick<Account, 'companyName' | 'slug' | 'avatarUrl'>;
  priceDisplay?: Pick<PriceDisplay, 'baseAmountCents' | 'nights'>;
  searchPromotionType?: Listing['searchPromotionType'];
};
export type AnalyticsQuote = {
  __typename: 'BookingQuote';
  totalWithoutTaxesCents?: number;
  totalCostCents?: number;
  checkInDate?: string;
  checkOutDate?: string;
  listing: {
    priceDisplay?: Pick<PriceDisplay, 'baseAmountCents' | 'subtotalWithoutTaxesCents'>;
  };
  quoteItems: Array<{
    costCents: number;
    type: QuoteItemizedType;
    description: string;
  }>;
};
export type AnalyticsQuoteComparison = {
  checkInDate: string;
  checkOutDate: string;
  externalListingId: string;
  externalSource: string;
  priceCents: number | null;
} | {
  externalSource: string;
};
export type AnalyticsReservation = {
  __typename: 'Reservation';
  id: string;
  totalCostCents?: number;
  startDate: string;
  endDate: string;
  listing: {
    slug: string;
  };
  guest?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    id?: string;
  };
};
export type AnalyticsGiftCard = Pick<GiftCard, 'orderAmountCents' | 'id' | 'shippingFeeCents'>;
export type AnalyticsSearch = {
  id: string;
  type?: ListingsSearchType;
  destination?: string;
  guestCount?: number;
  checkInDate?: string;
  checkOutDate?: string;
};

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_payment_info_item
export type ItemParams = {
  item_id: string; // required by GA4 item schema
  item_name: string; // required by GA4 item schema
  index?: number; // built-in item-level dimension
  price?: string; // built-in item-level dimension
  account_name?: string; // custom item-level dimension
  account_id?: string; // custom item-level dimension
  listing_id?: string; // custom item-level dimension
  listing_name?: string; // custom item-level dimension
  subscription_level?: string; // custom item-level dimension
  search_variant?: string; // custom item-level dimension
};

// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
export type EcomEventParams = {
  value: number | null;
  currency: string;
  check_in_date: string | null;
  check_out_date: string | null;
  quantity: number | null;
  transaction_id?: string;
  tax?: number | null;
  fees?: number | null;
  price_preview?: number | null;
  preview_value?: number | null;
  preview_fees?: number | null;
  airbnb_value?: number | null;
  vrbo_value?: number | null;
};
export type AnalyticsUser = {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
};

// List context mapping for view_item_list events
const LIST_CONTEXTS = {
  search: {
    item_list_id: 'search_results',
    item_list_name: 'Search Results'
  },
  serp: {
    item_list_id: 'serp_search_results',
    item_list_name: 'SERP - Search Results'
  }
} as const;
export type ListingListSource = keyof typeof LIST_CONTEXTS;

// Context
const AnalyticsContext = React.createContext<AnalyticsContext | undefined>(undefined);

// Private helper functions (pure, no component state dependencies)
const dl_push = (data: object) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...data
  });
};

// GA4's maximum length for an event parameter string value.
const GA4_STRING_LIMIT = 100;
const toListParam = (values: readonly string[]): string | null => values.length > 0 ? [...values].sort().join(',').slice(0, GA4_STRING_LIMIT) : null;
const toUserData = (user: AnalyticsUser) => {
  return {
    id: user.id,
    email: user.email,
    sha256_email_address: user.email ? sha256(user.email).toString() : null,
    address: {
      first_name: user.firstName,
      sha256_first_name: user.firstName ? sha256(user.firstName).toString() : null,
      last_name: user.lastName,
      sha256_last_name: user.lastName ? sha256(user.lastName).toString() : null
    }
  };
};
const parseQuoteParams = (quote: AnalyticsQuote): EcomEventParams => {
  const valueCents = quote.totalWithoutTaxesCents || quote.totalCostCents;
  const baseAmountCents = quote.listing?.priceDisplay?.baseAmountCents;
  const previewValue = quote.listing?.priceDisplay?.subtotalWithoutTaxesCents || quote.listing?.priceDisplay?.baseAmountCents;
  const feesValue = quote.listing?.priceDisplay?.subtotalWithoutTaxesCents ? quote.listing.priceDisplay.subtotalWithoutTaxesCents - quote.listing.priceDisplay.baseAmountCents : null;
  const categorizedQuoteItems = quote.quoteItems.reduce((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {} as Record<QuoteItemizedType, typeof quote.quoteItems>);
  const totalFeesCents = categorizedQuoteItems['FEE']?.reduce((acc, item) => acc + item.costCents, 0) || null;
  const totalTaxesCents = categorizedQuoteItems['TAX']?.reduce((acc, item) => acc + item.costCents, 0) || null;
  const numberOfNights = quote.checkInDate && quote.checkOutDate ? dayjs(quote.checkOutDate).diff(dayjs(quote.checkInDate), 'day') : null;
  return {
    value: valueCents ? valueCents / 100 : null,
    tax: totalTaxesCents ? totalTaxesCents / 100 : null,
    fees: totalFeesCents ? totalFeesCents / 100 : null,
    price_preview: baseAmountCents ? baseAmountCents / 100 : null,
    preview_value: previewValue ? previewValue / 100 : null,
    preview_fees: feesValue ? feesValue / 100 : null,
    check_in_date: quote.checkInDate || null,
    check_out_date: quote.checkOutDate || null,
    quantity: numberOfNights,
    currency: 'USD'
  };
};
const parseReservationParams = (reservation: AnalyticsReservation): EcomEventParams => {
  const valueCents = reservation.totalCostCents;
  const numberOfNights = dayjs(reservation.endDate).diff(dayjs(reservation.startDate), 'day');
  return {
    value: valueCents ? valueCents / 100 : null,
    check_in_date: reservation.startDate,
    check_out_date: reservation.endDate,
    quantity: numberOfNights,
    transaction_id: reservation.id,
    currency: 'USD'
  };
};
const toEcomEventParams = (source: AnalyticsQuote | AnalyticsReservation): EcomEventParams => {
  switch (source.__typename) {
    case 'BookingQuote':
      return parseQuoteParams(source);
    case 'Reservation':
      return parseReservationParams(source);
  }
};
const quoteComparisonsToEconEventParams = (quote: AnalyticsQuote, comparisonQuotes: AnalyticsQuoteComparison[]): EcomEventParams => {
  const valueCents = quote.totalWithoutTaxesCents;
  const numberOfNights = quote.checkInDate && quote.checkOutDate ? dayjs(quote.checkOutDate).diff(dayjs(quote.checkInDate), 'day') : null;
  let airbnb_value, vrbo_value;
  for (const comparisonQuote of comparisonQuotes) {
    if ('priceCents' in comparisonQuote) {
      if (comparisonQuote.externalSource === 'airbnb') {
        airbnb_value = comparisonQuote.priceCents;
      } else if (comparisonQuote.externalSource === 'vrbo') {
        vrbo_value = comparisonQuote.priceCents;
      }
    }
  }
  return {
    value: valueCents ? valueCents / 100 : null,
    airbnb_value: airbnb_value ? airbnb_value / 100 : null,
    vrbo_value: vrbo_value ? vrbo_value / 100 : null,
    check_in_date: quote.checkInDate || null,
    check_out_date: quote.checkOutDate || null,
    quantity: numberOfNights,
    currency: 'USD'
  };
};

// Provider Component
interface Props {
  children: React.ReactNode;
}
export const AnalyticsProvider: React.FC<Props> = t0 => {
  const $ = _c(32);
  const {
    children
  } = t0;
  const {
    data: session
  } = useSession();
  const [currentListing, setCurrentListingState] = React.useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [listings, setListingsState] = React.useState(t1);
  const [searchState, setSearchState] = React.useState(null);
  const [page] = React.useState(1);
  const [pageSize] = React.useState(30);
  const [currentExperiments, setCurrentExperimentsState] = React.useState(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = listing => {
      setCurrentListingState(listing);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const setCurrentListing = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = experiments => {
      setCurrentExperimentsState(experiments);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const setCurrentExperiments = t3;
  let t4;
  if ($[3] !== page || $[4] !== pageSize) {
    t4 = (listing_0, index) => {
      const baseItem = {
        item_id: listing_0.slug || "",
        item_name: listing_0.name || "",
        account_name: listing_0.account.companyName,
        account_id: listing_0.account.slug,
        listing_id: listing_0.slug,
        listing_name: listing_0.name,
        subscription_level: listing_0.listingLevel
      };
      if (index !== undefined) {
        baseItem.index = (page - 1) * pageSize + index + 1;
      }
      if (listing_0.priceDisplay) {
        baseItem.price = formatDollarAmountNumberOnly(toInteger(listing_0.priceDisplay.baseAmountCents / listing_0.priceDisplay.nights));
      }
      if (listing_0.searchPromotionType) {
        baseItem.search_variant = listing_0.searchPromotionType;
      }
      return baseItem;
    };
    $[3] = page;
    $[4] = pageSize;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const toItemParams = t4;
  let t5;
  if ($[6] !== toItemParams) {
    t5 = (source, listings_0) => {
      setListingsState(listings_0);
      dl_push({
        event: "view_item_list",
        ...LIST_CONTEXTS[source],
        listings: listings_0.map(toItemParams)
      });
    };
    $[6] = toItemParams;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const setListings = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = search => {
      if (!search) {
        return;
      }
      setSearchState(search);
      dl_push({
        event: "search",
        search_term: search.destination
      });
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const setSearch = t6;
  const emitViewQuoteEvent = _temp;
  const emitViewQuoteComparisonEvent = _temp2;
  const emitBeginQuoteCheckoutEvent = _temp3;
  const emitReservationPurchaseEvent = _temp4;
  const emitGiftCardPurchaseEvent = _temp5;
  const emitGenerateLeadEvent = _temp6;
  const emitSignUpBeginEvent = _temp7;
  const emitReferralJoinedProgramEvent = _temp8;
  const emitSignUpEvent = _temp9;
  const emitQuoteValidationFallbackEvent = _temp10;
  const emitSearchCheckInTooSoonEvent = _temp11;
  const emitSearchQueryResultsEvent = _temp12;
  const emitAccountActionFallbackEvent = _temp13;
  const emitTravelerSurveyEvent = _temp14;
  const emitTravelerGovIdVerificationEvent = _temp15;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  React.useEffect(_temp16, t7);
  let t8;
  let t9;
  if ($[10] !== session) {
    t8 = () => {
      if (session?.user) {
        dl_push({
          user_id: session.user.id,
          user_data: toUserData(session.user)
        });
      } else {
        dl_push({
          user_id: null,
          user_data: null
        });
      }
    };
    t9 = [session];
    $[10] = session;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  React.useEffect(t8, t9);
  let t10;
  if ($[13] !== searchState) {
    t10 = () => {
      const previousSearch = getSearchSession(undefined);
      if (!searchState && previousSearch) {
        setSearchState(previousSearch as AnalyticsSearch);
      }
    };
    $[13] = searchState;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  React.useEffect(t10, t11);
  let t12;
  let t13;
  if ($[16] !== currentListing || $[17] !== listings || $[18] !== toItemParams) {
    t12 = () => {
      if (currentListing) {
        const index_0 = listings.findIndex(listing_1 => listing_1.id === currentListing.id);
        dl_push({
          current_listings: [toItemParams(currentListing, index_0 == -1 ? undefined : index_0)]
        });
      } else {
        dl_push({
          current_listings: []
        });
      }
    };
    t13 = [currentListing, listings, toItemParams];
    $[16] = currentListing;
    $[17] = listings;
    $[18] = toItemParams;
    $[19] = t12;
    $[20] = t13;
  } else {
    t12 = $[19];
    t13 = $[20];
  }
  React.useEffect(t12, t13);
  let t14;
  let t15;
  if ($[21] !== searchState) {
    t14 = () => {
      if (searchState !== undefined) {
        dl_push({
          search_destination: searchState?.destination,
          search_type: searchState?.type,
          guest_count: searchState?.guestCount,
          check_in_date: searchState?.checkInDate,
          check_out_date: searchState?.checkOutDate
        });
        setSearchSession(undefined, searchState);
      }
    };
    t15 = [searchState];
    $[21] = searchState;
    $[22] = t14;
    $[23] = t15;
  } else {
    t14 = $[22];
    t15 = $[23];
  }
  React.useEffect(t14, t15);
  let t16;
  let t17;
  if ($[24] !== currentExperiments) {
    t16 = () => {
      dl_push({
        experiments: currentExperiments
      });
    };
    t17 = [currentExperiments];
    $[24] = currentExperiments;
    $[25] = t16;
    $[26] = t17;
  } else {
    t16 = $[25];
    t17 = $[26];
  }
  React.useEffect(t16, t17);
  let t18;
  if ($[27] !== setListings) {
    t18 = {
      setCurrentListing,
      setCurrentExperiments,
      setListings,
      setSearch,
      emitViewQuoteEvent,
      emitViewQuoteComparisonEvent,
      emitReservationPurchaseEvent,
      emitGiftCardPurchaseEvent,
      emitBeginQuoteCheckoutEvent,
      emitGenerateLeadEvent,
      emitReferralJoinedProgramEvent,
      emitSignUpBeginEvent,
      emitSignUpEvent,
      emitQuoteValidationFallbackEvent,
      emitSearchCheckInTooSoonEvent,
      emitSearchQueryResultsEvent,
      emitAccountActionFallbackEvent,
      emitTravelerSurveyEvent,
      emitTravelerGovIdVerificationEvent
    };
    $[27] = setListings;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const contextValue = t18;
  let t19;
  if ($[29] !== children || $[30] !== contextValue) {
    t19 = <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
    $[29] = children;
    $[30] = contextValue;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  return t19;
};
export const useAnalytics = () => {
  const context = React.useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};
function _temp(quote) {
  const params = toEcomEventParams(quote);
  dl_push({
    event: "view_quote",
    ...params
  });
}
function _temp2(quote_0, comparisonQuotes) {
  const params_0 = quoteComparisonsToEconEventParams(quote_0, comparisonQuotes);
  dl_push({
    event: "view_quote_comparison",
    ...params_0
  });
}
function _temp3(quote_1) {
  const ecomParams = toEcomEventParams(quote_1);
  dl_push({
    event: "begin_checkout",
    ...ecomParams
  });
}
function _temp4(reservation) {
  const ecomParams_0 = toEcomEventParams(reservation);
  dl_push({
    event: "purchase",
    ...ecomParams_0
  });
}
function _temp5(giftCard) {
  const orderAmount = giftCard.orderAmountCents / 100;
  const shippingAmount = giftCard.shippingFeeCents / 100;
  dl_push({
    event: "purchase",
    currency: "USD",
    value: orderAmount,
    transaction_id: giftCard.id,
    shipping: shippingAmount,
    items: [{
      item_name: "Gift Card",
      item_id: giftCard.id,
      price: orderAmount - shippingAmount,
      currency: "USD",
      quantity: 1
    }]
  });
}
function _temp6(lead) {
  dl_push({
    event: "generate_lead",
    lead_source: lead.source,
    account_name: lead.account_name
  });
}
function _temp7(provider) {
  dl_push({
    event: "sign_up_begin",
    provider
  });
}
function _temp8() {
  dl_push({
    event: "referral_joined_program"
  });
}
function _temp9(user, rewardCampaign) {
  dl_push({
    event: "sign_up",
    user_data: toUserData(user),
    ...(rewardCampaign && {
      reward_campaign: rewardCampaign
    })
  });
}
function _temp10(details) {
  dl_push({
    event: "quote_validation_fallback",
    error_clause: quoteValidationReportClause(details.rawMessage),
    listing_id: details.listingSlug,
    check_in_date: details.checkInDate,
    check_out_date: details.checkOutDate,
    adult_count: details.adultCount,
    child_count: details.childCount,
    infant_count: details.infantCount,
    pet_count: details.petCount
  });
}
function _temp11(details_0) {
  dl_push({
    event: "search_check_in_too_soon",
    surface: details_0.surface,
    check_in_date: details_0.checkInDate,
    check_out_date: details_0.checkOutDate,
    first_bookable_check_in_date: details_0.firstBookableCheckInDate,
    days_of_notice: dayjs(details_0.firstBookableCheckInDate).diff(dayjs(details_0.checkInDate), "day")
  });
}
function _temp12(t0) {
  const {
    queryIndex,
    resultCount,
    destination,
    query
  } = t0;
  dl_push({
    event: "search_query_results",
    query_index: queryIndex,
    result_count: resultCount,
    query_destination: destination?.slice(0, GA4_STRING_LIMIT) ?? null,
    place_id: query.placeId.slice(0, GA4_STRING_LIMIT),
    query_check_in_date: query.checkInDate,
    query_check_out_date: query.checkOutDate,
    adult_count: query.adultCount,
    child_count: query.childCount,
    infant_count: query.infantCount,
    pet_count: query.petCount,
    min_rooms: query.minNumberOfRooms,
    min_bathrooms: query.minNumberOfBathrooms,
    min_beds: query.minNumberOfBeds,
    min_price_cents: query.minPriceCents ?? null,
    max_price_cents: query.maxPriceCents ?? null,
    features: toListParam(query.featureKeys),
    property_types: toListParam(query.propertyTypes),
    north_east_lat: query.northEastLat ?? null,
    north_east_lng: query.northEastLng ?? null,
    south_west_lat: query.southWestLat ?? null,
    south_west_lng: query.southWestLng ?? null
  });
}
function _temp13(details_1) {
  dl_push({
    event: "account_action_fallback",
    step: details_1.step,
    failure: details_1.failure
  });
}
function _temp14(details_2) {
  dl_push({
    event: "traveler_survey",
    outcome: details_2.outcome,
    ...(details_2.choice && {
      choice: details_2.choice
    }),
    ...(details_2.message && {
      message: details_2.message
    })
  });
}
function _temp15(details_3) {
  dl_push({
    event: "traveler_gov_id_verification",
    outcome: details_3.outcome,
    ...(details_3.message && {
      message: details_3.message.slice(0, 100)
    })
  });
}
function _temp16() {
  dl_push({
    bot: isbot(navigator.userAgent)
  });
}